import { Pencil, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { usePotHistoryQuery } from '../../features/pots/queries';
import { useDeleteExpenseMutation } from '../../features/expenses/queries';
import { useExpenseSheet } from '../../features/expenses/SheetProvider';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { formatEUR } from '../../utils/money';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-PT');
  } catch {
    return iso;
  }
}

/**
 * CHANGE v2 §24.4: histórico do cofre — depósitos são despesas com
 * ``savings_pot=pot.id``. Editar/eliminar abre o sheet de despesas ou chama
 * directamente o endpoint de delete.
 */
export function PotHistoryDialog({ pot, isOpen, onClose }) {
  const { data: items = [], isLoading, refetch } = usePotHistoryQuery(pot?.id, {
    enabled: Boolean(pot?.id) && isOpen,
  });
  const deleteMutation = useDeleteExpenseMutation();
  const { open: openExpenseSheet } = useExpenseSheet();

  if (!pot) return null;

  const handleEdit = (expense) => {
    openExpenseSheet(null, { savingsPotId: pot.id });
    onClose();
  };

  const handleDelete = (expense) => {
    if (!window.confirm(pt.pots.history.deleteConfirm)) return;
    deleteMutation.mutate(expense.id, {
      onSuccess: () => {
        toast.success(pt.common.saved);
        refetch();
      },
      onError: (err) => {
        const message =
          (err && (err.detail || JSON.stringify(err))) || 'Erro ao eliminar.';
        toast.error(typeof message === 'string' ? message : 'Erro');
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pt.pots.history.title} · {pot.name}</DialogTitle>
          <DialogDescription>
            {pt.pots.kinds[pot.kind] || pot.kind}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {pt.common.loading}
          </p>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {pt.pots.history.empty}
          </p>
        ) : (
          <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
            {items.map((expense) => (
              <li
                key={expense.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground">
                    {formatEUR(Number(expense.amount || 0))}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(expense.occurred_on)}
                    {' · '}
                    {expense.description || pt.pots.history.noDescription}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(expense)}
                    aria-label={pt.pots.history.editCta}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(expense)}
                    disabled={deleteMutation.isPending}
                    aria-label={pt.pots.history.deleteCta}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            {pt.pots.history.back}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
